import { Type } from "@themes/pollinator/ui/Type";
import { SectionHeading } from "@themes/pollinator/ui/SectionHeading";
import { type DonutChartDataItem } from "@themes/pollinator/ui/DonutChart";
import { OrgInsightsDonutRow } from "./OrgInsightsDonutRow";
import { OrgInsightsComparisonList } from "./OrgInsightsComparisonList";
import type {
  Distribution,
  OrgInsights as OrgInsightsData,
  ProgramInsightsTopline,
} from "./orgInsights.types";

export type Props = {
  /**
   * Per-org data. `null` when the org has fewer than 3 onboarded
   * participants (or isn't in the program at all) — the component then
   * renders only the program-wide column with a "below threshold"
   * message.
   */
  orgInsights: OrgInsightsData | null;
  /** Program-wide topline, always shown. */
  topline: ProgramInsightsTopline;
};

/**
 * Sending Institution Insights — per-org demographics for the org admin's
 * tracking surface, with Pollinator-wide context for comparison.
 *
 * Two render states:
 *  - `orgInsights != null` → full split: org column + program column for
 *    every metric.
 *  - `orgInsights == null` → below-threshold message at top, program
 *    column only for every metric.
 *
 * Loading / publisher-missing states are handled by the container.
 */
export function OrgInsights({ orgInsights, topline }: Props) {
  const hasOrgData = orgInsights != null;
  const orgName = orgInsights?.name ?? "Your Organization";

  return (
    <div
      className="w-full flex flex-col gap-12"
      data-testid="org-insights"
    >
      <WelcomeIntro topline={topline} />

      <Type
        variant="caption"
        className="text-brand-700 uppercase tracking-wider"
      >
        {orgName} Data
      </Type>

      {hasOrgData ? (
        <OrgHeader
          orgInsights={orgInsights}
          topline={topline}
        />
      ) : (
        <BelowThresholdHeader topline={topline} />
      )}

      <OrgInsightsDonutRow
        title="Career stage"
        description={`Where ${orgName}'s participants are in their career.`}
        orgData={
          hasOrgData
            ? toOrderedDonut(orgInsights.career_stage_pct, CAREER_STAGE_ORDER)
            : undefined
        }
        programData={toOrderedDonut(
          stripUnderscoreKeys(topline.career_stage),
          CAREER_STAGE_ORDER
        )}
        orgN={hasOrgData ? orgInsights.career_stage_n : undefined}
        programN={topline.career_stage._n}
        orgLabel={orgName}
      />

      <OrgInsightsDonutRow
        title="Years practicing"
        description="Years in primary discipline."
        orgData={
          hasOrgData
            ? toOrderedDonut(
                orgInsights.years_practicing_pct,
                YEARS_PRACTICING_ORDER
              )
            : undefined
        }
        programData={toOrderedDonut(
          stripUnderscoreKeys(topline.years_practicing),
          YEARS_PRACTICING_ORDER
        )}
        orgN={hasOrgData ? orgInsights.years_practicing_n : undefined}
        programN={topline.years_practicing._n}
        orgLabel={orgName}
      />

      <OrgInsightsComparisonList
        title="Affinities"
        description="Multi-select: each participant may belong to multiple groups."
        orgData={
          hasOrgData
            ? relabelKeys(orgInsights.affinities_pct, AFFINITY_LABEL_MAP)
            : undefined
        }
        programData={relabelKeys(
          stripUnderscoreKeys(topline.affinities),
          AFFINITY_LABEL_MAP
        )}
        orgN={hasOrgData ? orgInsights.affinities_n : undefined}
        programN={topline.affinities._n}
        orgLabel={orgName}
      />

      <OrgInsightsComparisonList
        title="Prior roles in groups"
        description={`Roles ${orgName}'s participants have taken in prior group settings (multi-select).`}
        orgData={hasOrgData ? orgInsights.roles_taken_pct : undefined}
        programData={stripUnderscoreKeys(topline.roles_taken)}
        orgN={hasOrgData ? orgInsights.roles_taken_n : undefined}
        programN={topline.roles_taken._n}
        orgLabel={orgName}
      />

      <ExperienceInGroupsStat
        orgPct={
          hasOrgData ? orgInsights.experience_in_groups_yes_pct : undefined
        }
        orgN={hasOrgData ? orgInsights.experience_in_groups_n : undefined}
        programPct={topline.experience_in_groups_yes_pct}
        programN={topline.experience_in_groups_n}
        orgLabel={orgName}
      />

      <ClosingSection
        orgName={orgName}
        nUsers={hasOrgData ? orgInsights.n_users : undefined}
      />
    </div>
  );
}

export default OrgInsights;

// ---------- Sub-components ----------

function WelcomeIntro({ topline }: { topline: ProgramInsightsTopline }) {
  const participants = topline.population.ever_onboarded;
  const countries = topline.countries;
  const careerStage = topline.career_stage;
  const affinities = topline.affinities;

  const midOrEstablishedPct =
    careerStage._mid_or_established_pct ??
    (careerStage["Mid-career"] ?? 0) + (careerStage["Established"] ?? 0);
  const emergingPct =
    careerStage._emerging_pct ?? careerStage["Emerging"] ?? 0;

  const peerCoachingPct = topline.agreement?.peer_coaching_value_w1_only;
  const networkExpansionPct = topline.agreement?.network_expansion_w1_only;
  // Engagement and tech-worked are aggregated across all available
  // weekly surveys so the wrap-up framing stays consistent with the
  // other already-cumulative stats on the page (demographics, counts,
  // attendance). The two W1-only metrics below (peer coaching, network
  // expansion) are structurally W1-only — those questions weren't
  // asked in later weeks — so they stay as-is.
  const engagementPct = averageWeekValue(topline.agreement?.engagement);
  const techWorkedPct = averageWeekValue(topline.agreement?.tech_worked);
  const attendancePct =
    topline.attendance?.per_circle_any_week_4_5_6_pct;

  return (
    <div className="flex flex-col gap-12 pb-8 border-b border-brand-800">
      <section className="flex flex-col gap-4">
        <Type
          variant="caption"
          className="text-brand-700 uppercase tracking-wider"
        >
          Sending Institution
        </Type>
        <Type variant="body2" className="max-w-none">
          Thank you for inviting your alumni to engage in our second-ever
          program.
        </Type>
        <Type variant="body2" className="max-w-none">
          What happened after you sent 1 email and/or posted about
          Pollinator on social media?
        </Type>
        <Type variant="body2" className="max-w-none">
          They engaged in a 5-week program that uses secure video and
          custom peer-coaching curriculum.
        </Type>
      </section>

      <section className="flex flex-col gap-4">
        <Type
          variant="caption"
          className="text-brand-700 uppercase tracking-wider"
        >
          Pollinator Survey Results
        </Type>

        <div className="flex flex-col gap-2 pl-4 border-l-2 border-brand-800">
          <Type variant="body2" className="text-brand-700 italic max-w-none">
            "[The Pollinator session] reminded me of the value of
            community, connection, and how people can walk so many
            different paths in cultural fields while still{" "}
            <strong>finding lines of connection</strong>."
          </Type>
          <Type variant="caption" className="text-brand-700">
            — Mario Rodriguez
          </Type>
        </div>

        <Type variant="body2" className="max-w-none">
          Our <strong>{participants.toLocaleString()}</strong> participants
          come from <strong>{countries}</strong> countries, and are:
        </Type>
        <ul className="flex flex-col gap-1 pl-6 list-disc">
          <AffinityStat
            pct={midOrEstablishedPct}
            label="mid-career or established in their career"
          />
          <AffinityStat
            pct={emergingPct}
            label="emerging in their career"
          />
          <AffinityStat pct={affinities["BIPOC"]} label="BIPOC" />
          <AffinityStat pct={affinities["LGBTQIA"]} label="LGBTQIA" />
          <AffinityStat
            pct={affinities["HistMarg-Other"]}
            label="historically marginalized along other axes"
          />
          <AffinityStat
            pct={affinities["Woman/Nonbinary"]}
            label="women, nonbinary"
          />
          <AffinityStat
            pct={affinities["Caretaker/Parent"]}
            label="caretakers/parents"
          />
          <AffinityStat
            pct={affinities["Disabled/Chronically ill"]}
            label="disabled, chronically ill"
          />
        </ul>

        {peerCoachingPct != null && (
          <Type variant="body2" className="max-w-none">
            <strong>Peer coaching is a desired skill.</strong>{" "}
            {formatPct(peerCoachingPct)}% of participants say they value
            what they've learned so far about peer coaching.
          </Type>
        )}

        {engagementPct != null && (
          <Type variant="body2" className="max-w-none">
            <strong>Participants are engaged during meetings.</strong>{" "}
            {formatPct(engagementPct)}% of people reported that "Despite
            our busy lives, my group was engaged."
          </Type>
        )}

        {attendancePct != null && (
          <Type variant="body2" className="max-w-none">
            <strong>Attendance is high</strong> — especially for a free,
            online program. {formatPct(attendancePct)}% of peer circles
            have 4, 5, or 6 people showing up.
          </Type>
        )}

        {techWorkedPct != null && (
          <Type variant="body2" className="max-w-none">
            <strong>
              Our custom technology is working, with room for improvement.
            </strong>{" "}
            {formatPct(techWorkedPct)}% of participants say "the
            technology is working well for me." We built our secure video
            platform from scratch, and are refining it daily.
          </Type>
        )}

        {networkExpansionPct != null && (
          <Type variant="body2" className="max-w-none">
            <strong>Matching participants is magical.</strong>{" "}
            {formatPct(networkExpansionPct)}% of people say "I am
            expanding and deepening my network via my peer circle."
            Sentiment analysis from write-in responses also highlights the
            magic of connection as core to the Pollinator program.
          </Type>
        )}
      </section>
    </div>
  );
}

function AffinityStat({ pct, label }: { pct?: number; label: string }) {
  if (pct == null) return null;
  return (
    <li>
      <Type variant="body2" as="span">
        <strong>{formatPct(pct)}%</strong> {label}
      </Type>
    </li>
  );
}

/**
 * Simple average across all `w\d+` keys in an agreement map. Returns
 * undefined if no weekly keys exist. Each week is weighted equally —
 * we don't currently surface per-week respondent counts to support a
 * weighted mean, and for the May 2026 cycle the weekly Ns are close
 * enough that the simple-mean approximation matches a weighted mean
 * within a tenth of a point.
 */
function averageWeekValue(
  weekly: Record<string, number> | undefined
): number | undefined {
  if (!weekly) return undefined;
  const values = Object.entries(weekly)
    .filter(([k]) => /^w\d+$/.test(k))
    .map(([, v]) => v);
  if (!values.length) return undefined;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

function OrgHeader({
  orgInsights,
  topline,
}: {
  orgInsights: OrgInsightsData;
  topline: ProgramInsightsTopline;
}) {
  const connected = orgInsights.connected_orgs ?? [];
  return (
    <div className="flex flex-col gap-2">
      <Type variant="body2" className="max-w-none">
        <strong>{orgInsights.n_users.toLocaleString()}</strong>{" "}
        participants from <strong>{orgInsights.name}</strong>, across{" "}
        <strong>{orgInsights.n_distinct_disciplines}</strong> creative
        disciplines
        {connected.length > 0 ? (
          <> are connected to alumni from:</>
        ) : (
          <>.</>
        )}
      </Type>
      {connected.length > 0 && (
        <Type variant="body2" className="max-w-none">
          <strong>{connected.join(", ")}</strong>
        </Type>
      )}
      <Type variant="body2" className="text-brand-700 max-w-none">
        Program-wide:{" "}
        <strong>{topline.population.ever_onboarded.toLocaleString()}</strong>{" "}
        participants from <strong>{topline.countries}</strong> countries
        across <strong>{topline.orgs_total}</strong> sending organizations.
      </Type>
    </div>
  );
}

function BelowThresholdHeader({
  topline,
}: {
  topline: ProgramInsightsTopline;
}) {
  return (
    <div className="flex flex-col gap-2">
      <Type variant="body2" className="max-w-none">
        Org-specific insights become available when 3 or more of your
        participants onboard. You don't have enough onboarded participants
        to show org-specific demographics yet — here's the Pollinator-wide
        picture so far.
      </Type>
      <Type variant="body2" className="text-brand-700 max-w-none">
        <strong>{topline.population.ever_onboarded.toLocaleString()}</strong>{" "}
        participants from <strong>{topline.countries}</strong> countries
        across <strong>{topline.orgs_total}</strong> sending organizations.
      </Type>
    </div>
  );
}

function ExperienceInGroupsStat({
  orgPct,
  orgN,
  programPct,
  programN,
  orgLabel,
}: {
  orgPct?: number;
  orgN?: number;
  programPct: number;
  programN: number;
  orgLabel: string;
}) {
  return (
    <SectionHeading
      title="Experience in peer-to-peer groups"
      description="Participants who said yes when asked if they have prior peer-to-peer group experience."
      titleVariant="h4"
      descriptionVariant="body2"
    >
      <div className="flex flex-col gap-6 md:flex-row md:gap-16">
        {orgPct != null && (
          <div className="flex flex-col gap-1">
            <Type variant="caption" className="text-brand-700">
              {orgLabel}
              {orgN != null ? ` (n=${orgN})` : ""}
            </Type>
            <Type variant="h3" as="span">
              {formatPct(orgPct)}%
            </Type>
          </div>
        )}
        <div className="flex flex-col gap-1">
          <Type variant="caption" className="text-brand-700">
            Pollinator overall (n={programN})
          </Type>
          <Type variant="h3" as="span">
            {formatPct(programPct)}%
          </Type>
        </div>
      </div>
    </SectionHeading>
  );
}

function ClosingSection({
  orgName,
  nUsers,
}: {
  orgName: string;
  nUsers: number | undefined;
}) {
  const COST_PER_PARTICIPANT = 500;
  const valueProvided =
    nUsers != null ? nUsers * COST_PER_PARTICIPANT : undefined;

  return (
    <div className="flex flex-col gap-12 pt-8 border-t border-brand-800">
      <SectionHeading
        title="What did we learn?"
        description="Download the preliminary findings from the second-ever program cycle."
        titleVariant="h4"
        descriptionVariant="body2"
      >
        <Type variant="body2" className="max-w-none">
          <a
            href="https://pollinator-production.s3.amazonaws.com/production/assets/materials/PollinatorClosingSessionMay2026.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-700 underline"
          >
            Pollinator Closing Session — May 2026 (PDF)
          </a>
        </Type>
      </SectionHeading>

      <SectionHeading
        title="Summary"
        titleVariant="h4"
      >
        <div className="flex flex-col gap-4">
          <Type variant="body2" className="max-w-none">
            Pollinator provided{" "}
            <strong>
              {valueProvided != null
                ? `$${valueProvided.toLocaleString()}`
                : `$${COST_PER_PARTICIPANT} × # of participants`}
            </strong>{" "}
            at no cost to <strong>{orgName}</strong> in 2026.
          </Type>
          <Type variant="body2" className="max-w-none">Thanks for sending your people!</Type>
          <Type variant="body2" className="max-w-none">
            <a
              href="https://www.every.org/outlast-foundation/f/pollinator"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-700 underline"
            >
              Donate to Pollinator
            </a>
          </Type>
        </div>
      </SectionHeading>

      <SectionHeading
        title="Outreach in September and October for the November 2026 Cycle."
        titleVariant="h4"
      >
        <Type variant="body2" className="max-w-none">
          Invite an aligned organization:{" "}
          <a
            href="mailto:caroline@pollinator.coop"
            className="text-brand-700 underline"
          >
            caroline@pollinator.coop
          </a>
        </Type>
      </SectionHeading>
    </div>
  );
}

// ---------- Helpers ----------

/**
 * Canonical bucket order for single-select distributions. Drives both
 * donut slice order and color assignment so the same bucket gets the
 * same color in both the org and program donuts.
 */
const CAREER_STAGE_ORDER = ["Emerging", "Mid-career", "Established"] as const;

const YEARS_PRACTICING_ORDER = [
  "Fewer than 5 Years",
  "5+ Years",
  "10+ Years",
  "15+ Years",
  "20+ Years",
  "30+ Years",
  "40+ Years",
] as const;

/**
 * Palette for donut slices, used in index order against the canonical
 * bucket arrays above. Same index → same color in org and program donuts.
 *
 * Ordered to alternate dark/light/mid so adjacent slices contrast. The
 * pollinator palette is a single-hue green ramp; 700–100 in linear order
 * reads as a gradient and adjacent slices (e.g. brand-500 / brand-600,
 * both bright greens) are visually identical in a chart legend.
 */
const SLICE_COLORS = [
  "brand-800", // near-black green
  "brand-200", // bright yellow
  "brand-600", // spring green
  "brand-100", // pale yellow
  "brand-700", // dark green
  "brand-400", // olive
  "brand-500", // mid green (least-used position — wraparound)
];

function toOrderedDonut(
  dist: Distribution,
  order: readonly string[]
): DonutChartDataItem[] {
  // Preserve order; include zero-valued buckets so the legend stays
  // identical across the org and program donuts. Unknown labels (e.g.
  // a new bucket added in a future cycle) get appended at the end with
  // remaining colors.
  const inOrder: DonutChartDataItem[] = order.map((label, i) => ({
    label,
    value: dist[label] ?? 0,
    color: SLICE_COLORS[i % SLICE_COLORS.length],
  }));
  const knownSet = new Set(order);
  const extras: DonutChartDataItem[] = Object.keys(dist)
    .filter((label) => !knownSet.has(label))
    .map((label, i) => ({
      label,
      value: dist[label],
      color: SLICE_COLORS[(order.length + i) % SLICE_COLORS.length],
    }));
  return [...inOrder, ...extras];
}

/**
 * Display-layer rename for affinity bucket keys. The probe ships compact
 * keys (e.g. "HistMarg-Other"); this map swaps in human-readable labels
 * before they hit the comparison list.
 */
const AFFINITY_LABEL_MAP: Record<string, string> = {
  "HistMarg-Other": "Historically Marginalized (Other)",
};

function relabelKeys(
  dist: Distribution,
  map: Record<string, string>
): Distribution {
  const out: Distribution = {};
  for (const [k, v] of Object.entries(dist)) {
    out[map[k] ?? k] = v;
  }
  return out;
}

/**
 * Topline distributions include reserved `_n` / `_mid_or_established_pct`
 * keys that aren't real buckets. Strip them before charting.
 */
function stripUnderscoreKeys(dist: Distribution): Distribution {
  const out: Distribution = {};
  for (const [key, value] of Object.entries(dist)) {
    if (!key.startsWith("_")) out[key] = value;
  }
  return out;
}

function formatPct(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}
